import { useId } from "react";
import { equipmentDefinition } from "@frontier/content/arsenal";
import { PAINTS } from "@frontier/content/season";
import type { SeasonView } from "@frontier/content/season";
import { HULL_DISPLAY_ART } from "./hull-art";
import { EquipmentArt } from "./EquipmentArt";
import { ResourceSymbol } from "./ResourceSymbol";
import { credits } from "./ui-labels";
import "./season-rewards.css";

type Reward = SeasonView["rewards"][number];

export function SeasonRewardArt({ reward }: { reward: Reward }) {
  const paintFilter = useId();
  if (reward.equipment) return <EquipmentArt equipment={reward.equipment} />;
  if (reward.paint) {
    const paint = PAINTS[reward.paint];
    return (
      <svg
        viewBox="0 0 180 110"
        role="img"
        aria-label={`Окраска «${paint.name}»`}
      >
        <defs>
          <filter id={paintFilter} colorInterpolationFilters="sRGB">
            <feComponentTransfer>
              <feFuncR type="linear" slope={((paint.tint >> 16) & 255) / 255} />
              <feFuncG type="linear" slope={((paint.tint >> 8) & 255) / 255} />
              <feFuncB type="linear" slope={(paint.tint & 255) / 255} />
            </feComponentTransfer>
          </filter>
        </defs>
        <image
          href={HULL_DISPLAY_ART.interceptor}
          width="180"
          height="110"
          filter={`url(#${paintFilter})`}
        />
      </svg>
    );
  }
  return (
    <ResourceSymbol kind={!reward.premiumOnly ? "credits" : "components"} />
  );
}

export function seasonRewardName(reward: Reward): string {
  if (reward.equipment)
    return `${equipmentDefinition(reward.equipment).name} MK ${"I".repeat(reward.equipment.tier + 1)}`;
  if (reward.paint) return PAINTS[reward.paint].name;
  return "Ресурсы";
}

export function SeasonRewardCard({
  reward,
  level,
  premium,
  busy,
  onClaim,
  onPremium,
}: {
  reward: Reward;
  level: number;
  premium: boolean;
  busy: boolean;
  onClaim: (level: number) => void;
  onPremium: () => void;
}) {
  const reached = level >= reward.level;
  const lockedTrack = reward.premiumOnly && !premium;
  const state = reward.claimed
    ? "claimed"
    : !reached
      ? "future"
      : lockedTrack
        ? "locked"
        : "ready";
  return (
    <div
      className={`season-reward season-${reward.premiumOnly ? "premium" : "free"} season-reward-${state} ${!reward.paint && !reward.equipment ? "season-resource-bundle" : ""}`}
      data-reward-state={state}
    >
      <div className="season-reward-topline">
        <small>{!reward.premiumOnly ? "ДОСТУПНО ВСЕМ" : "ПРЕМИУМ"}</small>
        {reward.claimed && <span aria-hidden="true">✓</span>}
        {!reward.claimed && (lockedTrack || !reached) && (
          <span aria-hidden="true">◇</span>
        )}
      </div>
      <div
        className={`season-reward-art ${reward.paint ? "season-paint-art" : ""}`}
      >
        <SeasonRewardArt reward={reward} />
      </div>
      <b className="season-reward-name">{seasonRewardName(reward)}</b>
      {reward.equipment && (
        <span className="season-reward-description">чертёж и 1 экземпляр</span>
      )}
      {reward.paint && (
        <span className="season-reward-description">Окраска корпуса</span>
      )}
      <div className="season-reward-resources">
        <span>
          <ResourceSymbol kind="credits" />
          {credits(reward.credits)} <small>КР</small>
        </span>
        <span>
          <ResourceSymbol kind="components" />
          {reward.components.toLocaleString("ru-RU")} <small>комп.</small>
        </span>
      </div>
      <button
        className="station-button"
        disabled={busy || state === "claimed" || state === "future"}
        onClick={() => {
          if (busy) return;
          if (state === "locked") onPremium();
          else if (state === "ready") onClaim(reward.level);
        }}
      >
        {reward.claimed
          ? "Получено"
          : !reached
            ? "Ещё не достигнут"
            : lockedTrack
              ? "Открыть премиум"
              : "Забрать"}
      </button>
    </div>
  );
}
